import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, Send, Eye, X, Newspaper, Users, Plus, Trash2, Link, Image, Upload, Settings, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Header from '@/components/Header';
import SendingProgressModal from '@/components/SendingProgressModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsBroadcast = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [recipientCount, setRecipientCount] = useState({ total: 0, breakdown: {} });
  
  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressJobId, setProgressJobId] = useState('');
  
  // Sender accounts state
  const [senderAccounts, setSenderAccounts] = useState([]);
  const [selectedSender, setSelectedSender] = useState(null);
  
  const [formData, setFormData] = useState({
    magazine: 'Aabkari Times',
    subject: 'Weekly News Update',
    greeting: 'Good Morning!',
    articles: [
      { title: '', image_url: '', link: '', description: '' }
    ],
    sponsor: { name: '', image_url: '' },
    recipient_types: [],
    sender_email: '',
    sender_name: ''
  });

  const recipientOptions = [
    { value: 'all_members', label: 'All Members', group: 'Members' },
    { value: 'active_members', label: 'Active Members', group: 'Members' },
    { value: 'inactive_members', label: 'Inactive Members', group: 'Members' },
    { value: 'complimentary_members', label: 'Complimentary Members', group: 'Members' },
    { value: 'all_advertisers', label: 'All Advertisers', group: 'Advertisers' },
    { value: 'active_advertisers', label: 'Active Advertisers', group: 'Advertisers' },
    { value: 'inactive_advertisers', label: 'Inactive Advertisers', group: 'Advertisers' },
    { value: 'complimentary_advertisers', label: 'Complimentary Advertisers', group: 'Advertisers' }
  ];

  // Fetch sender accounts when magazine changes
  useEffect(() => {
    fetchSenderAccounts();
  }, [formData.magazine]);

  useEffect(() => {
    if (formData.recipient_types.length > 0) {
      fetchRecipientCount();
    } else {
      setRecipientCount({ total: 0, breakdown: {} });
    }
  }, [formData.recipient_types, formData.magazine]);

  const fetchSenderAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email/sender-accounts/${formData.magazine}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSenderAccounts(response.data || []);
      
      // Set default sender if available
      const defaultSender = response.data?.find(a => a.is_default);
      if (defaultSender && !selectedSender) {
        setSelectedSender(defaultSender);
        setFormData(prev => ({
          ...prev,
          sender_email: defaultSender.email,
          sender_name: defaultSender.display_name
        }));
      }
    } catch (error) {
      console.error('Error fetching sender accounts:', error);
    }
  };

  const handleSenderChange = (accountId) => {
    const account = senderAccounts.find(a => a.id === accountId);
    if (account) {
      setSelectedSender(account);
      setFormData(prev => ({
        ...prev,
        sender_email: account.email,
        sender_name: account.display_name
      }));
    }
  };

  const fetchRecipientCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/news-broadcast/count-recipients`, {
        magazine: formData.magazine,
        recipient_types: formData.recipient_types
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipientCount(response.data);
    } catch (error) {
      console.error('Error fetching recipient count:', error);
    }
  };

  const handleRecipientToggle = (value) => {
    setFormData(prev => ({
      ...prev,
      recipient_types: prev.recipient_types.includes(value)
        ? prev.recipient_types.filter(t => t !== value)
        : [...prev.recipient_types, value]
    }));
  };

  const handleArticleChange = (index, field, value) => {
    const newArticles = [...formData.articles];
    newArticles[index][field] = value;
    setFormData({ ...formData, articles: newArticles });
  };

  const fetchUrlMetadata = async (index, url) => {
    if (!url) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/news-broadcast/fetch-url-metadata`, { url }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const newArticles = [...formData.articles];
        newArticles[index] = {
          ...newArticles[index],
          title: response.data.title || newArticles[index].title,
          image_url: response.data.image || newArticles[index].image_url,
          description: response.data.description || newArticles[index].description,
          link: url
        };
        setFormData({ ...formData, articles: newArticles });
        toast.success('Article details fetched successfully!');
      } else if (response.data.manual_entry_required) {
        // SSL/TLS issue - inform user to enter manually
        toast.error(
          'Could not fetch from website (SSL issue). Please enter the article title, image URL, and description manually.',
          { duration: 6000 }
        );
      } else {
        toast.error(response.data.error || 'Could not fetch URL metadata');
      }
    } catch (error) {
      console.error('Error fetching URL metadata:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || 'Failed to fetch URL metadata';
      if (errorMsg.includes('SSL') || errorMsg.includes('TLS')) {
        toast.error(
          'Website has SSL configuration issues. Please enter article details manually (title, image URL, description).',
          { duration: 6000 }
        );
      } else {
        toast.error(`Error: ${errorMsg}`);
      }
    }
  };

  const addArticle = () => {
    if (formData.articles.length < 10) {
      setFormData({
        ...formData,
        articles: [...formData.articles, { title: '', image_url: '', link: '', description: '' }]
      });
    }
  };

  const removeArticle = (index) => {
    if (formData.articles.length > 1) {
      const newArticles = formData.articles.filter((_, i) => i !== index);
      setFormData({ ...formData, articles: newArticles });
    }
  };

  const handlePreview = async () => {
    const validArticles = formData.articles.filter(a => a.title.trim());
    if (validArticles.length === 0) {
      toast.error('Please add at least one article with a title');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/news-broadcast/preview`, {
        magazine: formData.magazine,
        greeting: formData.greeting,
        articles: validArticles,
        sponsor: formData.sponsor
      });
      setPreviewHtml(response.data.html);
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const validArticles = formData.articles.filter(a => a.title.trim());
    if (validArticles.length === 0) {
      toast.error('Please add at least one article');
      return;
    }
    if (formData.recipient_types.length === 0) {
      toast.error('Please select at least one recipient group');
      return;
    }

    setSending(true);
    setShowPreview(false);
    
    try {
      const token = localStorage.getItem('token');
      
      // Start progress tracking
      const progressResponse = await axios.post(`${API}/email/progress/start`, {
        total: recipientCount.total,
        email_type: 'news_broadcast'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const jobId = progressResponse.data.job_id;
      setProgressJobId(jobId);
      setShowProgressModal(true);
      
      const response = await axios.post(`${API}/news-broadcast/send`, {
        ...formData,
        articles: validArticles,
        job_id: jobId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`News broadcast sent to ${response.data.sent_count} recipient(s)!`);
    } catch (error) {
      console.error('Error sending news broadcast:', error);
      toast.error(error.response?.data?.detail || 'Failed to send news broadcast');
      setShowProgressModal(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-sky-50 py-8">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#0ea5e9' }}>
              News Broadcast
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Left - Form */}
          <div className="col-span-2 space-y-6">
            {/* Magazine & Subject */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h2 className="font-semibold mb-4 flex items-center">
                <Mail className="mr-2" size={18} />
                Email Details
              </h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Magazine</Label>
                  <select
                    value={formData.magazine}
                    onChange={(e) => setFormData({ ...formData, magazine: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="Aabkari Times">Aabkari Times</option>
                    <option value="Sugar Times">Sugar Times</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Subject Line *</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Weekly News Update"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Greeting</Label>
                  <Input
                    value={formData.greeting}
                    onChange={(e) => setFormData({ ...formData, greeting: e.target.value })}
                    placeholder="Good Morning!"
                  />
                </div>
              </div>
              
              {/* Sender Email Selection */}
              <div className="mt-4 bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Label className="font-semibold text-indigo-800">Send From:</Label>
                    <select
                      value={selectedSender?.id || ''}
                      onChange={(e) => handleSenderChange(e.target.value)}
                      className="h-10 w-72 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm"
                    >
                      {senderAccounts.length === 0 ? (
                        <option value="">No sender accounts configured</option>
                      ) : (
                        senderAccounts.map(account => (
                          <option key={account.id} value={account.id}>
                            {account.display_name} ({account.email})
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  {senderAccounts.length === 0 && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => navigate('/email-settings')}
                      className="text-indigo-700 border-indigo-300"
                    >
                      <Settings className="mr-1" size={14} />
                      Configure
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Articles */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold flex items-center">
                  <Newspaper className="mr-2" size={18} />
                  News Articles ({formData.articles.length}/10)
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addArticle}
                  disabled={formData.articles.length >= 10}
                >
                  <Plus className="mr-1" size={14} />
                  Add Article
                </Button>
              </div>
              
              <div className="space-y-4">
                {formData.articles.map((article, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-sky-700">Article {index + 1}</span>
                      {formData.articles.length > 1 && (
                        <button
                          onClick={() => removeArticle(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                    
                    {/* URL Input with Auto-Fetch */}
                    <div className="mb-3 space-y-2">
                      <Label className="text-xs flex items-center font-semibold"><Link size={12} className="mr-1" /> Article URL *</Label>
                      <div className="flex gap-2">
                        <Input
                          value={article.link}
                          onChange={(e) => handleArticleChange(index, 'link', e.target.value)}
                          placeholder="https://aabkaritimes.com/news/article-name"
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => fetchUrlMetadata(index, article.link)}
                          disabled={!article.link}
                        >
                          Auto-Fetch
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500">Paste the article URL and click &quot;Auto-Fetch&quot; to automatically get title, image, and description</p>
                    </div>
                    
                    {/* Auto-filled or Manual Fields */}
                    <div className="grid grid-cols-2 gap-3 border-t pt-3 mt-3">
                      <div className="col-span-2 space-y-2">
                        <Label className="text-xs">Title {article.title && '✓'}</Label>
                        <Input
                          value={article.title}
                          onChange={(e) => handleArticleChange(index, 'title', e.target.value)}
                          placeholder="Article headline (auto-fetched or enter manually)..."
                        />
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label className="text-xs flex items-center"><Image size={12} className="mr-1" /> Featured Image URL {article.image_url && '✓'}</Label>
                        <Input
                          value={article.image_url}
                          onChange={(e) => handleArticleChange(index, 'image_url', e.target.value)}
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label className="text-xs">Description/Excerpt {article.description && '✓'}</Label>
                        <Textarea
                          value={article.description}
                          onChange={(e) => handleArticleChange(index, 'description', e.target.value)}
                          placeholder="Brief description of the article..."
                          rows={2}
                        />
                      </div>
                    </div>
                    {article.image_url && (
                      <div className="mt-3 border rounded p-2 bg-white">
                        <img src={article.image_url} alt="Preview" className="max-h-24 rounded" onError={(e) => e.target.style.display = 'none'} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Sponsor */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h2 className="font-semibold mb-4 flex items-center">
                <Image className="mr-2" size={18} />
                Sponsor Section (Optional)
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sponsor Name</Label>
                  <Input
                    value={formData.sponsor.name}
                    onChange={(e) => setFormData({ ...formData, sponsor: { ...formData.sponsor, name: e.target.value } })}
                    placeholder="Company Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sponsor Logo/Image URL <span className="text-xs text-gray-500">(Recommended: 200x60px, max 2MB)</span></Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.sponsor.image_url}
                      onChange={(e) => setFormData({ ...formData, sponsor: { ...formData.sponsor, image_url: e.target.value } })}
                      placeholder="https://example.com/logo.png"
                      className="flex-1"
                    />
                    <input
                      type="file"
                      id="sponsorLogoUpload"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 2 * 1024 * 1024) {
                          toast.error('File size exceeds 2MB limit');
                          return;
                        }
                        try {
                          const formDataUpload = new FormData();
                          formDataUpload.append('file', file);
                          const token = localStorage.getItem('token');
                          const response = await axios.post(`${API}/upload/news_broadcast/sponsor_logos`, formDataUpload, {
                            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                          });
                          setFormData(prev => ({ 
                            ...prev, 
                            sponsor: { ...prev.sponsor, image_url: response.data.url } 
                          }));
                          toast.success('Sponsor logo uploaded!');
                        } catch (error) {
                          console.error('Upload error:', error);
                          toast.error('Failed to upload sponsor logo');
                        }
                      }}
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => document.getElementById('sponsorLogoUpload').click()}
                    >
                      <Upload className="mr-1" size={14} /> Upload
                    </Button>
                  </div>
                </div>
              </div>
              {(formData.sponsor.name || formData.sponsor.image_url) && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Sponsored By</p>
                  {formData.sponsor.image_url && (
                    <img src={formData.sponsor.image_url} alt={formData.sponsor.name} className="max-h-16 mx-auto mb-2" />
                  )}
                  {formData.sponsor.name && (
                    <p className="font-medium" style={{ color: (formData.magazine === 'Aabkari Times' || formData.magazine === 'Aabkari Times ENG') ? '#1e3a5f' : '#166534' }}>
                      {formData.sponsor.name}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Recipients */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h2 className="font-semibold mb-4 flex items-center">
                <Users className="mr-2" size={18} />
                Select Recipients *
              </h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-blue-700 mb-3">Members</h3>
                  <div className="space-y-2">
                    {recipientOptions.filter(o => o.group === 'Members').map(option => (
                      <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.recipient_types.includes(option.value)}
                          onChange={() => handleRecipientToggle(option.value)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">{option.label}</span>
                        {recipientCount.breakdown[option.value] > 0 && (
                          <span className="text-xs text-gray-500">({recipientCount.breakdown[option.value]})</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-green-700 mb-3">Advertisers</h3>
                  <div className="space-y-2">
                    {recipientOptions.filter(o => o.group === 'Advertisers').map(option => (
                      <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.recipient_types.includes(option.value)}
                          onChange={() => handleRecipientToggle(option.value)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">{option.label}</span>
                        {recipientCount.breakdown[option.value] > 0 && (
                          <span className="text-xs text-gray-500">({recipientCount.breakdown[option.value]})</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Summary & Actions */}
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border sticky top-6">
              <h2 className="font-semibold mb-4">Summary</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Magazine:</span>
                  <span className="font-medium">{formData.magazine}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Articles:</span>
                  <span className="font-medium">{formData.articles.filter(a => a.title.trim()).length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Recipients:</span>
                  <span className="font-medium text-sky-600">{recipientCount.total} email(s)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Sponsor:</span>
                  <span className={formData.sponsor.name ? 'text-green-600' : 'text-gray-400'}>
                    {formData.sponsor.name || 'None'}
                  </span>
                </div>
              </div>
              
              <div className="mt-6 space-y-3">
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={handlePreview}
                  disabled={loading}
                >
                  <Eye className="mr-2" size={16} />
                  {loading ? 'Loading...' : 'Preview Email'}
                </Button>
                <Button
                  className="w-full"
                  style={{ backgroundColor: '#0ea5e9' }}
                  onClick={handleSend}
                  disabled={sending || recipientCount.total === 0}
                >
                  <Send className="mr-2" size={16} />
                  {sending ? 'Sending...' : `Send to ${recipientCount.total} Recipients`}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
              <div className="bg-sky-500 text-white p-4 flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center">
                  <Eye className="mr-2" size={24} />
                  Email Preview
                </h2>
                <button onClick={() => setShowPreview(false)} className="text-white hover:text-gray-200">
                  <X size={24} />
                </button>
              </div>
              <div className="overflow-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
                <div className="p-4 bg-gray-100">
                  <div className="bg-white rounded-lg shadow mx-auto" style={{ maxWidth: '650px' }}>
                    <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                  </div>
                </div>
              </div>
              <div className="p-4 border-t flex justify-end space-x-3">
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  Close
                </Button>
                <Button
                  style={{ backgroundColor: '#0ea5e9' }}
                  onClick={handleSend}
                  disabled={sending || recipientCount.total === 0}
                >
                  <Send className="mr-2" size={16} />
                  {sending ? 'Sending...' : `Confirm & Send`}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Sending Progress Modal */}
        <SendingProgressModal
          isOpen={showProgressModal}
          onClose={() => setShowProgressModal(false)}
          jobId={progressJobId}
          totalRecipients={recipientCount.total}
          emailType="News Broadcast"
        />
      </div>
    </div>
  );
};

export default NewsBroadcast;
